import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { CommonRouteQueryComposable } from '@/composables/routeQueries/useCommonRouteQuery';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import { useUserStore } from '@/stores/useUserStore';
import * as dateHelpers from '@/helpers/date';
import { fetchGetAirroadBooking2 } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface JobsAirRoadViewDataComposable {
  isFetchingAirRoadBookingJobsData: Readonly<Ref<boolean>>;

  airRoadBookingJobsData: Readonly<
    Ref<ReadonlyDeep<openapi.JobsToAirroad2ListContentOpenapi> | undefined>
  >;

  airRoadBookingJobsDataError: Readonly<Ref<unknown | undefined>>;

  refreshAirRoadBookingJobsData: () => Promise<void>;

  reload: () => JobsAirRoadViewDataComposable;
}

export default function useJobsAirRoadViewData(
  routeQuery: CommonRouteQueryComposable,
): JobsAirRoadViewDataComposable {
  const route = useRoute();
  const userStore = useUserStore();
  const {
    queryCurPage,
    queryPageSize,
    queryDates,
    queryCustomer,
    queryStates,
    querySearch,
    queryOnly920Driver,
    queryExclude920Driver,
    queryFromWarehouse,
    queryToWarehouse,
  } = routeQuery;

  const userTimeZone = computed(() => userStore.user.timezone);

  const loaderStore = useLoaderStore();

  let airRoadBookingJobsDataController: AbortController | undefined;

  const airRoadBookingJobsDataFetchCounter = ref(0);

  const isFetchingAirRoadBookingJobsData = computed(
    () => !!airRoadBookingJobsDataFetchCounter.value,
  );

  const airRoadBookingJobsData =
    ref<ReadonlyDeep<openapi.JobsToAirroad2ListContentOpenapi>>();

  const airRoadBookingJobsDataError = ref();

  function clear(): void {
    if (airRoadBookingJobsDataController) {
      airRoadBookingJobsDataController.abort();
    }
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchAirRoadBookingJobsData(): Promise<
    ReadonlyDeep<openapi.JobsToAirroad2ListContentOpenapi> | undefined
  > {
    if (airRoadBookingJobsDataController) {
      airRoadBookingJobsDataController.abort();
    }
    if (!queryDates.value) return;
    const apiZonedFormattedDates = [
      dateHelpers.getApiZonedFormattedDate(
        queryDates.value[0],
        userTimeZone.value,
      ),
      dateHelpers.getApiZonedFormattedDate(
        queryDates.value[1],
        userTimeZone.value,
      ),
    ];
    const endpointParams = {
      search: querySearch.value,
      rockhopper_date_gte: apiZonedFormattedDates[0],
      rockhopper_date_lte: apiZonedFormattedDates[1],
      customer_code: queryCustomer.value,
      quick_booking: false,
      page: queryCurPage.value,
      page_size: queryPageSize.value,
      wh_state: queryStates.value?.join(','),
      only_920_driver: queryOnly920Driver.value,
      exclude_920_driver: queryExclude920Driver.value,
      from_warehouse: queryFromWarehouse.value,
      to_warehouse: queryToWarehouse.value,
    };
    airRoadBookingJobsDataController = new AbortController();
    return await fetchGetAirroadBooking2(endpointParams, {
      signal: airRoadBookingJobsDataController.signal,
    })
      .then(function (data) {
        airRoadBookingJobsDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        airRoadBookingJobsDataError.value = err;
        throw err;
      });
  }

  async function refreshAirRoadBookingJobsData(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    airRoadBookingJobsDataFetchCounter.value++;
    try {
      airRoadBookingJobsData.value = await _fetchAirRoadBookingJobsData();
    } finally {
      airRoadBookingJobsDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  function reload(): JobsAirRoadViewDataComposable {
    void refreshAirRoadBookingJobsData();
    return {
      isFetchingAirRoadBookingJobsData,
      airRoadBookingJobsData,
      airRoadBookingJobsDataError,
      refreshAirRoadBookingJobsData,
      reload,
    };
  }

  watch(
    [
      queryCurPage,
      queryPageSize,
      queryDates,
      queryCustomer,
      queryStates,
      querySearch,
      queryOnly920Driver,
      queryExclude920Driver,
      queryFromWarehouse,
      queryToWarehouse,
    ],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.AIR_ROAD_JOBS) return;
      reload();
    },
    { immediate: true },
  );

  return {
    isFetchingAirRoadBookingJobsData,
    airRoadBookingJobsData,
    airRoadBookingJobsDataError,
    refreshAirRoadBookingJobsData,
    reload,
  };
}
